/* Pro Direct Tools: groups the Kaching cart's side-panel suggestions into sections ("Batteries & chargers",
   "Caulking guns", ...) by Shopify product type (Will, 11 Oct). It never moves or replaces Kaching's own elements:
   it only tags each suggestion with its section, and pdt-cart.css orders the cards and draws the headings. Kaching's
   buttons, prices and product choice are untouched. Also asks for sharper (400px) images in place of the 200px ones.
   Filler cards (Will, 11 Oct: "add another product to the grey blank space"): when a section leaves empty grid
   cells, it adds our own cards (class pdt-fill, never Kaching's classes) with other in-stock products of the same
   type from that section's collection, matching the cart's battery system where possible. Their button adds via
   /cart/add.js and then calls kachingCartApi.refreshCart(). Compare-at prices are deliberately not shown. */
(() => {
  const GROUPS = [
    ['Batteries & chargers', ['Battery & Charger']],
    ['Paint sprayers', ['Paint Sprayer']],
    ['Caulking guns', ['Caulking Gun']],
    ['Drills & drivers', ['Hammer Drill', 'Impact Drill', 'Impact Wrench']],
    ['Nail guns', ['Nail Gun']],
    ['Saws & grinders', ['Angle Grinder', 'Circular Saw', 'Cutting Saw', 'Reciprocating Saw', 'Multi-Tool', 'Orbital Sander']],
    ['Leaf blowers', ['Leaf Blower']],
    ['Garden & farm', ['Grass Trimmer', 'Hedge Trimmer', 'Chainsaw', 'Pressure Washer', 'Grease Gun']],
    ['Kits', ['Complete Pro Kit']],
    ['Accessories', ['Accessory']]
  ];
  const SOURCE = {
    'Batteries & chargers': ['batteries-chargers'], 'Paint sprayers': ['paint-sprayers'], 'Caulking guns': ['caulking-guns'],
    'Drills & drivers': ['hammer-drills', 'impact-wrenches'], 'Nail guns': ['nail-guns'],
    'Saws & grinders': ['angle-grinders', 'circular-saws', 'multi-tools'], 'Leaf blowers': ['leaf-blowers'],
    'Garden & farm': ['farm-agriculture'], 'Kits': ['complete-kits'], 'Accessories': ['cases-accessories']
  };
  const groupOf = (type) => {
    for (const [label, types] of GROUPS) if (types.includes(type)) return label;
    return 'More tools';
  };
  const rank = (label) => {
    const i = GROUPS.findIndex(([l]) => l === label);
    return i === -1 ? GROUPS.length : i;
  };

  const KEY = 'pdt-cart-types';
  let cache = {};
  try { cache = JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch (e) { cache = {}; }
  const pending = {};
  const typeOf = (handle) => {
    if (cache[handle] !== undefined) return Promise.resolve(cache[handle]);
    if (!pending[handle]) {
      pending[handle] = fetch(`/products/${handle}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          cache[handle] = p ? p.type || '' : '';
          try { sessionStorage.setItem(KEY, JSON.stringify(cache)); } catch (e) { /* storage off: fine */ }
          return cache[handle];
        })
        .catch(() => '');
    }
    return pending[handle];
  };

  const colCache = {};
  const productsFor = (label) => {
    if (!colCache[label]) {
      colCache[label] = Promise.all((SOURCE[label] || []).map((h) =>
        fetch(`/collections/${h}/products.json?limit=30`).then((r) => (r.ok ? r.json() : { products: [] })).then((j) => j.products || []).catch(() => [])
      )).then((lists) => lists.flat());
    }
    return colCache[label];
  };
  const money = (v) => '£' + Number(v).toFixed(2);
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const sized = (src) => (src ? src + (src.includes('?') ? '&' : '?') + 'width=400' : '');

  const fillerCard = (p, rankNo) => {
    const li = document.createElement('li');
    li.className = 'pdt-fill';
    li.dataset.pdtFiller = '';
    li.style.setProperty('--pdt-g', rankNo);
    const live = p.variants.filter((v) => v.available);
    const opts = live.length > 1
      ? `<select class="pdt-fill__select" aria-label="Option">${live.map((v) => `<option value="${v.id}">${esc(v.title)}</option>`).join('')}</select>` : '';
    li.innerHTML = `<a class="pdt-fill__img" href="/products/${p.handle}"><img loading="lazy" src="${sized(p.images[0] && p.images[0].src)}" alt="${esc(p.title)}"></a>
      <a class="pdt-fill__title" href="/products/${p.handle}">${esc(p.title)}</a>
      <div class="pdt-fill__price"><span class="money">${money(live[0].price)}</span></div>
      <div class="pdt-fill__foot">${opts}<button type="button" class="pdt-fill__add" data-variant="${live[0].id}">Add to cart</button></div>`;
    const sel = li.querySelector('select');
    if (sel) sel.addEventListener('change', () => {
      const v = live.find((x) => String(x.id) === sel.value);
      li.querySelector('.pdt-fill__add').dataset.variant = v.id;
      li.querySelector('.money').textContent = money(v.price);
    });
    li.querySelector('.pdt-fill__add').addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      btn.disabled = true; btn.textContent = 'Adding…';
      try {
        const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(btn.dataset.variant), quantity: 1 }] }) });
        if (!r.ok) throw new Error('add failed');
        if (window.kachingCartApi && window.kachingCartApi.refreshCart) await window.kachingCartApi.refreshCart();
        btn.textContent = 'Added';
      } catch (err) {
        btn.textContent = 'Try again';
      } finally {
        setTimeout(() => { btn.disabled = false; btn.textContent = 'Add to cart'; }, 1800);
      }
    });
    return li;
  };

  const fill = async (list, rows, labels) => {
    const cols = getComputedStyle(list).gridTemplateColumns.split(' ').filter(Boolean).length;
    // only rebuild when the columns, the suggestions or the cart lines change. Kaching re-renders often (its
    // countdown ticks every second), and rebuilding each time made the filler cards flash.
    const inCart = [...document.querySelectorAll('#kaching-cart .kaching-cart-item__title')].map((a) => a.getAttribute('href')).join('|');
    const sig = [cols, labels.join(','), rows.map((li) => li.querySelector('[data-product-handle]').dataset.productHandle).join(','), inCart].join('#');
    if (list.dataset.pdtSig === sig && (cols < 2 || list.querySelector('li[data-pdt-filler]') || list.dataset.pdtNoFill)) return;
    list.dataset.pdtSig = sig;
    delete list.dataset.pdtNoFill;
    list.querySelectorAll('li[data-pdt-filler]').forEach((n) => n.remove());
    if (cols < 2) return;
    const counts = {};
    labels.forEach((l) => { counts[l] = (counts[l] || 0) + 1; });
    const shown = new Set(rows.map((li) => li.querySelector('[data-product-handle]').dataset.productHandle));
    let cart = { items: [] };
    try { cart = await (await fetch('/cart.js')).json(); } catch (e) { /* no cart info: still fill */ }
    cart.items.forEach((i) => shown.add(i.handle));
    const titles = cart.items.map((i) => i.product_title.toLowerCase()).join(' ');
    const eco = ['makita', 'dewalt', 'milwaukee'].find((b) => titles.includes(b));
    for (const label of Object.keys(counts)) {
      const gap = (cols - (counts[label] % cols)) % cols;
      if (!gap) continue;
      const pool = (await productsFor(label)).filter((p) => !shown.has(p.handle) && groupOf(p.product_type) === label && p.variants.some((v) => v.available) && p.images.length);
      pool.sort((a, b) => (eco ? (b.title.toLowerCase().includes(eco) - a.title.toLowerCase().includes(eco)) : 0));
      pool.slice(0, gap).forEach((p) => { shown.add(p.handle); list.appendChild(fillerCard(p, rank(label))); });
    }
    if (!list.querySelector('li[data-pdt-filler]')) list.dataset.pdtNoFill = '1';
  };

  // refill when the panel is resized into a different number of columns (e.g. Kaching's expand toggle)
  const watched = new WeakSet();
  const watchColumns = (list) => {
    if (watched.has(list) || !('ResizeObserver' in window)) return;
    watched.add(list);
    let cols = getComputedStyle(list).gridTemplateColumns.split(' ').filter(Boolean).length;
    new ResizeObserver(() => {
      const now = getComputedStyle(list).gridTemplateColumns.split(' ').filter(Boolean).length;
      if (now !== cols) { cols = now; schedule(); }
    }).observe(list);
  };

  let running = false;
  const apply = async () => {
    if (running) return;
    running = true;
    try {
      const list = document.querySelector('#kaching-cart .kaching-cart__vertical-carousel-list');
      if (!list) return;
      const rows = [...list.children].filter((li) => !li.hasAttribute('data-pdt-filler') && li.querySelector('[data-product-handle]'));
      if (!rows.length) return;
      const types = await Promise.all(rows.map((li) => typeOf(li.querySelector('[data-product-handle]').dataset.productHandle)));
      const seen = new Set();
      const labels = types.map(groupOf);
      rows.forEach((li, i) => {
        const label = labels[i];
        if (li.dataset.pdtGroup !== label) li.dataset.pdtGroup = label;
        li.style.setProperty('--pdt-g', rank(label));
        if (!seen.has(label)) { seen.add(label); li.setAttribute('data-pdt-first', ''); }
        else li.removeAttribute('data-pdt-first');
        const img = li.querySelector('img');
        if (img && img.src.includes('_200x200')) img.src = img.src.replace('_200x200', '_400x400');
      });
      list.classList.add('pdt-grouped');
      watchColumns(list);
      await fill(list, rows, labels);
    } finally {
      running = false;
    }
  };

  let timer;
  const schedule = () => { clearTimeout(timer); timer = setTimeout(apply, 60); };
  const start = () => {
    const root = document.getElementById('kaching-cart') || document.body;
    const ours = (n) => n.nodeType === 1 && (n.matches('[data-pdt-filler]') || n.closest('[data-pdt-filler]'));
    new MutationObserver((records) => {
      if (records.every((r) => [...r.addedNodes, ...r.removedNodes].every(ours) && (r.addedNodes.length || r.removedNodes.length) || ours(r.target))) return;
      const relevant = records.some((r) => {
        const t = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        if (!t || !t.closest) return false;
        if (t.closest('.kaching-cart__announcement-bar, .kaching-cart__tiered-promotions-bar, .kaching-cart__footer')) return false;
        return true;
      });
      if (!relevant) return;
      schedule();
    }).observe(root, { childList: true, subtree: true });
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
